import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import {
  constructIdempotencyKey,
  isDuplicateRequestCancelError,
  RequestConfig,
} from '~/common/services/requests';
import { RequestState } from '../../../../api/constants/RequestState';
import { EMPTY_UNRESOLVED_PROMISE } from '../../../../constants';
import { useEventHandler } from '../../../../hooks/useEventHandler';
import { useIsRefreshingSession } from '../../../../hooks/useIsRefreshingSession';
import { useToast } from '../../../../lib/chakraUi/hooks';
import { Nullable } from '../../../../types';
import { decodeJwtSub } from '../../../../utils/authentication';
import {
  unauthenticatedSession,
  useAuthApi,
  useAuthData,
  useAuthorizedInceptRequest,
  useAuthTokensRefreshData,
} from '../../../AuthContext';
import { useSetReturnUrl } from '../../../ReturnUrlContext';
import { useTenantedApplicationConfig } from '../../../tenantedApplicationConfig';
import { TransformerSet } from '../../constants/TransformerSet';
import { PlatformRequestOptions } from '../../types/PlatformRequestOptions';
import { addTransformersToConfig } from './logic/addTransformersToConfig';
import { captureRequestError } from './logic/captureRequestError';
import { createDeferredPromise } from './logic/createDeferredPromise';
import { launchErrorToast } from './logic/launchErrorToast';
import { makeTenantedConfig } from './logic/makeTenantedConfig';

export const usePlatformRequest = () => {
  const tenantConfig = useTenantedApplicationConfig();
  const performAuthorizedRequest = useAuthorizedInceptRequest();
  const toast = useToast();
  const { tokenRefreshState } = useAuthTokensRefreshData();
  const isRefreshingSession = useIsRefreshingSession();
  const { setSession } = useAuthApi();
  const tokens = useAuthData();
  const setReturnUrl = useSetReturnUrl();
  const location = useLocation();

  const deferredRef =
    useRef<Nullable<ReturnType<typeof createDeferredPromise>>>(null);
  const tokenRefreshStateRef = useRef(tokenRefreshState);
  const isRefreshingSessionRef = useRef(isRefreshingSession);

  // Keep the refresh-state ref current for the 401 handler below.
  useEffect(() => {
    tokenRefreshStateRef.current = tokenRefreshState;
  }, [tokenRefreshState]);

  // Hold requests while a stored session's expired token is being refreshed, and
  // release them once the refresh settles (token valid again, or refresh failed).
  // Keyed on isRefreshingSession rather than only `Loading`, so a request that
  // fires on the first render (before the refresh transitions to Loading) is
  // still held instead of going out with the expired token and 401ing.
  useEffect(() => {
    isRefreshingSessionRef.current = isRefreshingSession;
    if (!isRefreshingSession && deferredRef.current) {
      deferredRef.current.resolve();
      deferredRef.current = null;
    }
  }, [isRefreshingSession]);

  const waitForSessionRefresh = useEventHandler(async (): Promise<void> => {
    if (isRefreshingSessionRef.current) {
      if (!deferredRef.current) {
        deferredRef.current = createDeferredPromise();
      }
      await deferredRef.current.promise;
    }
  });

  const performRequest = useEventHandler(
    async <ResponseType>(config: RequestConfig) => {
      const response = await performAuthorizedRequest<ResponseType>(config);
      return response.data;
    },
  );

  const errorCapturingRequest = useEventHandler(
    async <ResponseType>(
      config: RequestConfig,
      preventDefaultErrorToast?: boolean,
    ) => {
      try {
        return await performRequest<ResponseType>(config);
      } catch (e: any) {
        if (e?.response?.status === 401) {
          // Skip when refresh just failed — onError already captured the path
          // before navigation.
          if (
            tokenRefreshStateRef.current !== RequestState.Error &&
            tokens?.jwt
          ) {
            setReturnUrl(
              location.pathname + location.search,
              decodeJwtSub(tokens.jwt),
            );
          }
          setSession(unauthenticatedSession());
          return Promise.reject(e);
        }

        if (isDuplicateRequestCancelError(e)) {
          return EMPTY_UNRESOLVED_PROMISE();
        }

        // Captured here rather than left to the toast: the toast withholds detail
        // from the user, and React Query parks rejections without surfacing them,
        // so this is the only place left to report the failure to Sentry.
        captureRequestError(e);

        if (!preventDefaultErrorToast) {
          launchErrorToast(toast, e, constructIdempotencyKey(config));
        }
        throw e;
      }
    },
  );

  const performPlatformRequest = useEventHandler(
    async <ResponseType>(
      config: RequestConfig,
      options?: PlatformRequestOptions,
    ) => {
      await waitForSessionRefresh();

      const tenantedRequestConfig = makeTenantedConfig(config, tenantConfig);
      const transformedRequestConfig = addTransformersToConfig(
        tenantedRequestConfig,
        options?.transformerSet ?? TransformerSet.Default,
      );
      return errorCapturingRequest<ResponseType>(
        transformedRequestConfig,
        options?.preventDefaultErrorToast,
      );
    },
  );

  return performPlatformRequest;
};
